import React, { Suspense } from "react";
import { createBrowserRouter } from "react-router-dom";
import Login from "../pages/Login";
import Users from "../pages/Users";
import Plans from "../pages/Plans";
import NotFound from "../pages/404";
import Orders from "../pages/Orders";
import Profile from "../pages/Profile";
import EditUser from "../pages/EditUser";
import Accounts from "../pages/Accounts";
import Settings from "../pages/Settings";
import Dashboard from "../pages/Dashboard";
import CreateUser from "../pages/CreateUser";
import CreateOrder from "../pages/BookOrder";
import TrackOrder from "../pages/TrackOrder";
import VerifyEmail from "../pages/VerifyEmail";
import EditAccount from "../pages/EditAccount";
import LandingPage from "../pages/LandingPage";
import SettingsPage from "../pages/SettingsPage";
import ProtectedRoute from "./auth/ProtectedRoute";
import MergeOrderWrapper from "../pages/MergeOrder";
import NoAccount from "../components/atoms/NoAccount";
import ForgotPassword from "../pages/ForgotPasswordOld";
import PostBookingPage from "../pages/PostBookingPage";
import TwoFactorPage from "../pages/TwoFactorPage";
import SkeltonFallback from "../pages/SkeltonFallback";
import LayoutWrapper from "../components/organisms/LayoutWrapper";
import Staff from "../pages/Staff";
import Support from "../pages/Support";
import StaffAnalytics from "../pages/StaffAnalytics";
import ShopifyOrders from "../pages/ShopifyOrders";
import TrackingPage from "../components/atoms/TrackingPage";
import Preferences from "../pages/Preferences";
import FinancePage from "../pages/FinancePage";
import StaffOrdersAnalytics from "../pages/StaffOrdersAnalytics";
import CourierOrderAnalytics from "../pages/CourierOrderAnalytics";
import CheckUploader from "../pages/CheckUploader";
import { UnauthorizedAccess } from "../pages/Unauth";
import ExportSettingsPage from "../pages/ExportSettingsPage";
import ExportRulesCreate from "../pages/ExportRulesCreate";
import ExportRulesEdit from "../pages/UpdateExportRule";

// Lazy load docs pages
const OrdersDocs = React.lazy(() => import("../docs/pages/Orders"));
const SettingsDocs = React.lazy(() => import("../docs/pages/Settings"));
const UsersManagement = React.lazy(() => import("../docs/pages/Users"));
const DashboardDocs = React.lazy(() => import("../docs/pages/Dashboard"));
const GettingStarted = React.lazy(() => import("../docs/GettingStarted"));
const BookOrderDocs = React.lazy(() => import("../docs/pages/BookOrder"));
const LiveTracking = React.lazy(() => import("../docs/pages/LiveTracking"));
const TwofactorAuth = React.lazy(() => import("../docs/pages/TwofactorAuth"));
const MergeOrdersDocs = React.lazy(() => import("../docs/pages/MergeOrders"));
const OrderBookingGuide = React.lazy(() => import("../docs/OrderBookingGuide"));
const TrackingAccountsDocs = React.lazy(() => import("../docs/pages/Accounts"));
const TrackingDocs = React.lazy(() => import("../docs/pages/Tracking"));
const CheckUploaderDocs = React.lazy(() => import("../docs/pages/CheckUploader"));
const PreferencesDocs = React.lazy(() => import("../docs/pages/Preferences"));
const AccessScopesDocs = React.lazy(() => import("../docs/pages/AccessScopes"));
const CourierOrderAnalyticsDocs = React.lazy(() => import("../docs/pages/CourierOrderAnalytics"));
const StaffAnalyticsDocs = React.lazy(() => import("../docs/pages/StaffAnalytics"));

const router = createBrowserRouter([
  {
    path: "/",
    element: <LandingPage />,
  },
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/unauthorized-access",
    element: <UnauthorizedAccess />,
  },
  {
    path: "/create-new-password",
    element: <ForgotPassword />,
  },

  {
    path: "/two-factor",
    element: <TwoFactorPage />,
  },
  {
    path: "/track/:track_number",
    element: <TrackOrder />,
  },
  {
    path: "/email-verify",
    element: <VerifyEmail />,
  },
  {
    path: "*",
    element: <NotFound />,
  },
  // Protected routes
  {
    path: "/dashboard",
    element: <ProtectedRoute />,
    children: [
      {
        element: <LayoutWrapper />,
        children: [
          { path: "", element: <Dashboard /> },
          { path: "users", element: <Users /> },
          { path: "orders", element: <Orders /> },
          { path: "shopify/orders", element: <ShopifyOrders /> },
          { path: "profile", element: <Profile /> },
          { path: "accounts", element: <Accounts /> },
          { path: "settings", element: <SettingsPage /> },
          { path: "export-rules", element: <ExportSettingsPage /> },
          { path: "export-rules/create", element: <ExportRulesCreate /> },
          { path: "export-rules/:id/edit", element: <ExportRulesEdit /> },
          { path: "order/book", element: <CreateOrder /> },
          { path: "create-user", element: <CreateUser /> },
          { path: "users/:user_id", element: <EditUser /> },
          { path: "post-booking", element: <PostBookingPage /> },
          { path: "tracking", element: <TrackingPage /> },
          { path: "create-account-alert", element: <NoAccount /> },
          { path: "merge-orders", element: <MergeOrderWrapper /> },
          { path: "accounts/create-account", element: <Settings /> },
          { path: "edit-account/:account_id", element: <EditAccount /> },
          { path: "plans", element: <Plans /> },
          { path: "support", element: <Support /> },
          { path: "staff", element: <Staff /> },
          { path: "preferences", element: <Preferences /> },
          { path: "finance", element: <FinancePage /> },
          { path: "staff-analytics", element: <StaffAnalytics /> },
          { path: "couriers-analytics", element: <CourierOrderAnalytics /> },
          { path: "cheque-uploader", element: <CheckUploader /> },
          // { path: "staff/:user_id/stats", element: <StaffAnalytics /> },
          // Docs - BookEase
          {
            path: "docs",
            element: <GettingStarted />,
          },

          {
            path: "docs/dashboard",
            element: (
              <Suspense fallback={<SkeltonFallback />}>
                <DashboardDocs />
              </Suspense>
            ),
          },
          {
            path: "docs/staff-analytics",
            element: (
              <Suspense fallback={<SkeltonFallback />}>
                <StaffAnalyticsDocs />
              </Suspense>
            ),
          },
          {
            path: "docs/couriers-analytics",
            element: (
              <Suspense fallback={<SkeltonFallback />}>
                <CourierOrderAnalyticsDocs />
              </Suspense>
            ),
          },
          {
            path: "docs/orders",
            element: (
              <Suspense fallback={<SkeltonFallback />}>
                <OrdersDocs />
              </Suspense>
            ),
          },
          {
            path: "docs/tracking-accounts",
            element: (
              <Suspense fallback={<SkeltonFallback />}>
                <TrackingAccountsDocs />
              </Suspense>
            ),
          },
          {
            path: "docs/order-booking",
            element: (
              <Suspense fallback={<SkeltonFallback />}>
                <BookOrderDocs />
              </Suspense>
            ),
          },
          {
            path: "docs/settings",
            element: (
              <Suspense fallback={<SkeltonFallback />}>
                <SettingsDocs />
              </Suspense>
            ),
          },
          {
            path: "docs/merge-orders",
            element: (
              <Suspense fallback={<SkeltonFallback />}>
                <MergeOrdersDocs />
              </Suspense>
            ),
          },
          {
            path: "docs/users",
            element: (
              <Suspense fallback={<SkeltonFallback />}>
                <UsersManagement />
              </Suspense>
            ),
          },
          {
            path: "docs/order-booking-guide",
            element: (
              <Suspense fallback={<SkeltonFallback />}>
                <OrderBookingGuide />
              </Suspense>
            ),
          },
          {
            path: "docs/two-factor-auth",
            element: (
              <Suspense fallback={<SkeltonFallback />}>
                <TwofactorAuth />
              </Suspense>
            ),
          },
          {
            path: "docs/live-tracking",
            element: (
              <Suspense fallback={<SkeltonFallback />}>
                <LiveTracking />
              </Suspense>
            ),
          },
          {
            path: "docs/tracking",
            element: (
              <Suspense fallback={<SkeltonFallback />}>
                <TrackingDocs />
              </Suspense>
            ),
          },
          {
            path: "docs/cheque-uploader",
            element: (
              <Suspense fallback={<SkeltonFallback />}>
                <CheckUploaderDocs />
              </Suspense>
            ),
          },
          {
            path: "docs/preferences",
            element: (
              <Suspense fallback={<SkeltonFallback />}>
                <PreferencesDocs />
              </Suspense>
            ),
          },
          {
            path: "docs/access-scopes",
            element: (
              <Suspense fallback={<SkeltonFallback />}>
                <AccessScopesDocs />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
]);

export default router;
